/**
 * Reusable QR code component.
 * Supports display, download, and copy functionality.
 */

import { useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';

interface QRCodeProps {
  /** The value to encode in the QR code */
  value: string;
  /** Size of the QR code in pixels (default: 200) */
  size?: number;
  /** Optional title displayed above the QR code */
  title?: string;
  /** Optional description displayed below the QR code */
  description?: string;
  /** Show download button (default: true) */
  downloadable?: boolean;
  /** Filename for download (default: 'qrcode') */
  downloadFilename?: string;
  /** Show copy button for the value (default: false) */
  copyable?: boolean;
  /** Custom CSS class for the container */
  className?: string;
  /** Level of error correction (L, M, Q, H) */
  level?: 'L' | 'M' | 'Q' | 'H';
  /** Include a small logo/image in the center */
  imageUrl?: string;
}

export default function QRCode({
  value,
  size = 200,
  title,
  description,
  downloadable = true,
  downloadFilename = 'qrcode',
  copyable = false,
  className = '',
  level = 'M',
  imageUrl,
}: QRCodeProps) {
  const qrRef = useRef<HTMLDivElement>(null);

  const handleDownload = useCallback(() => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    // Create a canvas to render the SVG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with padding
    const padding = 20;
    canvas.width = size + padding * 2;
    canvas.height = size + padding * 2;

    // Fill white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Convert SVG to data URL
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, padding, padding, size, size);
      URL.revokeObjectURL(svgUrl);

      // Download as PNG
      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${downloadFilename}.png`;
      link.href = pngUrl;
      link.click();
    };
    img.src = svgUrl;
  }, [size, downloadFilename]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      // Could add toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [value]);

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {title && (
        <h4 className="text-sm font-medium text-content-primary mb-2">{title}</h4>
      )}

      <div
        ref={qrRef}
        className="bg-white p-4 rounded-lg border border-border shadow-sm"
      >
        <QRCodeSVG
          value={value}
          size={size}
          level={level}
          includeMargin={false}
          fgColor="#000000"
          bgColor="#ffffff"
          imageSettings={
            imageUrl
              ? {
                  src: imageUrl,
                  height: size * 0.15,
                  width: size * 0.15,
                  excavate: true,
                }
              : undefined
          }
        />
      </div>

      {description && (
        <p className="text-xs text-content-tertiary mt-2 text-center max-w-[250px]">
          {description}
        </p>
      )}

      {(downloadable || copyable) && (
        <div className="flex gap-2 mt-3">
          {downloadable && (
            <button
              onClick={handleDownload}
              className="text-sm px-3 py-1.5 text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 hover:bg-orbital-50 rounded-md transition-colors"
            >
              <svg
                className="w-4 h-4 inline-block mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download
            </button>
          )}
          {copyable && (
            <button
              onClick={handleCopy}
              className="text-sm px-3 py-1.5 text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 hover:bg-orbital-50 rounded-md transition-colors"
            >
              <svg
                className="w-4 h-4 inline-block mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copy
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Generate a TOTP otpauth:// URI for QR code enrollment.
 */
export function generateTOTPUri(
  secret: string,
  accountName: string,
  issuer: string = 'Passport'
): string {
  const encodedAccount = encodeURIComponent(accountName);
  const encodedIssuer = encodeURIComponent(issuer);
  return `otpauth://totp/${encodedIssuer}:${encodedAccount}?secret=${secret}&issuer=${encodedIssuer}&algorithm=SHA1&digits=6&period=30`;
}

/**
 * Generate an OrbitalPassport enrollment URI for QR code.
 */
export function generateEnrollmentUri(
  enrollmentToken: string,
  serviceId: string,
  gatewayUrl: string = 'https://passport.epy.digital'
): string {
  return `orbital://enroll?token=${enrollmentToken}&service=${serviceId}&gateway=${encodeURIComponent(gatewayUrl)}`;
}
